/**
 * The frame around every sign-in, sign-up and reset step: the
 * `AuthSplitLayout` (@tonnie/ui) with the portal's brand panel — the five
 * heroes as the journey (Citizen), the four pipeline stages and the hero each
 * runs on (Business), every step with its hero's icon —
 * a way back to the previous step, and the portal's language picker, the only
 * way to change language before signing in.
 */
import { ArrowLeft, HeroIcon, type HeroType } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { AuthSplitLayout, type AuthSplitLayoutProps } from '@tonnie/ui';
import { useMemo, type ReactNode } from 'react';

import { useAccountPortal, type AccountPortal } from '../portal';

type Props = Omit<AuthSplitLayoutProps, 'brand' | 'topEnd' | 'topStart'> & {
  /** Which headline the panel carries. */
  brand: 'sign-in' | 'sign-up';
  /** A way back to the previous step (`onBack`) — or none. */
  onBack?: () => void;
  children: ReactNode;
};

type Translate = ReturnType<typeof useTranslation>['t'];

const stepIcon = (hero: HeroType) => <HeroIcon hero={hero} className="h-[18px] w-[18px]" />;

function brandPanel(portal: AccountPortal, brand: Props['brand'], t: Translate) {
  const footer = t('auth.brand.copyright', { year: new Date().getFullYear() });
  switch (portal) {
    case 'citizen':
      return {
        name: t('auth.brand.name'),
        headline:
          brand === 'sign-in'
            ? t('auth.brand.citizen.signInHeadline')
            : t('auth.brand.citizen.signUpHeadline'),
        subline:
          brand === 'sign-in'
            ? t('auth.brand.citizen.signInSubline')
            : t('auth.brand.citizen.signUpSubline'),
        steps: [
          {
            icon: stepIcon('checkup'),
            title: 'CheckUp',
            description: t('auth.brand.citizen.checkup'),
          },
          {
            icon: stepIcon('teleportal'),
            title: 'TelePortal',
            description: t('auth.brand.citizen.teleportal'),
          },
          {
            icon: stepIcon('qultureup'),
            title: 'QultureUp',
            description: t('auth.brand.citizen.qultureup'),
          },
          { icon: stepIcon('care'), title: 'Care', description: t('auth.brand.citizen.care') },
          {
            icon: stepIcon('passport'),
            title: 'Passport',
            description: t('auth.brand.citizen.passport'),
          },
        ],
        footer,
      };
    case 'business':
      return {
        name: t('auth.brand.name'),
        headline:
          brand === 'sign-in'
            ? t('auth.brand.business.signInHeadline')
            : t('auth.brand.business.signUpHeadline'),
        subline:
          brand === 'sign-in'
            ? t('auth.brand.business.signInSubline')
            : t('auth.brand.business.signUpSubline'),
        steps: [
          {
            icon: stepIcon('checkup'),
            title: t('business.pipeline.stages.vetting'),
            description: t('auth.brand.business.vetting'),
          },
          {
            icon: stepIcon('teleportal'),
            title: t('business.pipeline.stages.mobility'),
            description: t('auth.brand.business.mobility'),
          },
          {
            icon: stepIcon('qultureup'),
            title: t('business.pipeline.stages.adaptation'),
            description: t('auth.brand.business.adaptation'),
          },
          {
            icon: stepIcon('care'),
            title: t('business.pipeline.stages.care'),
            description: t('auth.brand.business.care'),
          },
        ],
        footer,
      };
    default: {
      const exhaustive: never = portal;
      throw new Error(`Unhandled portal: ${String(exhaustive)}`);
    }
  }
}

export function AccountAuthLayout({ brand, onBack, ...rest }: Props) {
  const { t } = useTranslation();
  const { portal, languageSelector } = useAccountPortal();
  const panel = useMemo(() => brandPanel(portal, brand, t), [portal, brand, t]);

  return (
    <AuthSplitLayout
      brand={panel}
      topStart={
        onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 rounded text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="auth-back"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t('auth.flow.back')}
          </button>
        ) : null
      }
      topEnd={languageSelector}
      {...rest}
    />
  );
}
